import type { components } from '@social-studio/api-client';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { setFormatContext } from '../../lib/format';
import { hasSession } from '../../lib/session';

export type Me = components['schemas']['MeDto'];

export const ME_QUERY_KEY = ['me'] as const;

export class MeRequestError extends Error {
  constructor(readonly status: number) {
    super(`GET /v1/me answered ${status}`);
  }
}

/** The signed-in user, memberships and active tenant (spec 10 §5); re-read after a tenant switch. */
export function useMe() {
  return useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: async (): Promise<Me> => {
      const { data, response } = await api.GET('/v1/me');
      if (!response.ok || !data) throw new MeRequestError(response.status);
      // Dates in the shell (spec 05 §6) and the settings area (spec 10 §6) show
      // in the tenant time zone once it is known.
      if (data.active_tenant) setFormatContext({ timeZone: data.active_tenant.time_zone });
      return data;
    },
    // Without a session cookie there is nothing to ask; the gate sends the guest to /sign-in.
    enabled: hasSession(),
    retry: false,
    staleTime: 60_000,
  });
}
